import { ArrowLeftFromLine } from 'lucide-react';
import { motion } from 'framer-motion';
import { Divider, Flex, IconButton, Text, useBreakpointValue } from '@chakra-ui/react';
import { useLocation, type Location } from 'react-router-dom';
import { useSubSidebar } from './useSubSidebar';
import { SubSidebarMenuButton } from './SubSidebarMenuButton';
import { MenuItemsType } from './MenuItems';
import { sizeValues } from '../../constants/layout';

// Determines which sidebar item should be highlighted for the current route.
// First tries exact path matching (e.g. /units/content-roles matches the Roles item).
// For criteria items, compares only the specified query params (e.g. ?outcome=PENDING).
// If nothing matches exactly, falls back to the longest prefix match, so
// /units/abc123/assessment highlights the /units item.
function findActiveLink(items: MenuItemsType[], location: Location): string | undefined {
  const currentPath = location.pathname;
  const currentSearch = new URLSearchParams(location.search);

  for (const item of items) {
    const [linkPath, linkSearch] = item.link.split('?');
    if (currentPath !== linkPath) {
      continue;
    }
    if (item.criteria && item.criteria.length > 0) {
      const linkParams = new URLSearchParams(linkSearch);
      const criteriaMatch = item.criteria.every(
        (param) => currentSearch.get(param) === linkParams.get(param),
      );
      if (criteriaMatch) {
        return item.link;
      }
    } else if (!linkSearch) {
      return item.link;
    } else if (location.search === `?${linkSearch}`) {
      return item.link;
    }
  }

  let bestMatch: string | undefined;
  let bestLength = 0;
  for (const item of items) {
    const linkPath = item.link.split('?')[0] ?? '';
    if (currentPath.startsWith(linkPath + '/') && linkPath.length > bestLength) {
      bestMatch = item.link;
      bestLength = linkPath.length;
    }
  }
  return bestMatch;
}

export function SideDrawer({
  items = [],
  label,
  showCollapseButton = true,
}: {
  items: MenuItemsType[];
  label: string;
  showCollapseButton?: boolean;
}): React.ReactElement | null {
  const location = useLocation();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const activeLink = findActiveLink(items, location);
  const sidebarWidth = useBreakpointValue(
    {
      base: sizeValues.subSidebarSm,
      lg: sizeValues.subSidebar,
    },
    { fallback: 'lg' },
  );

  return (
    <motion.div
      initial={false}
      animate={{ width: isExpanded ? sidebarWidth : '0' }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      style={{ overflow: 'hidden', height: '100%' }}
    >
      <Flex
        direction="column"
        alignItems="flex-start"
        justifyContent="center"
        gap={2}
        py={4}
        px={2}
        flexShrink="0"
        overflow="auto"
        h="100%"
      >
        <Flex gap={2} alignItems="center" alignSelf="stretch" justifyContent="space-between" pl={2}>
          <Text textStyle="lg" fontWeight="medium" textTransform="capitalize">
            {label}
          </Text>

          {showCollapseButton === true && (
            <IconButton
              aria-label="collapse secondary sidebar"
              icon={<ArrowLeftFromLine />}
              variant="tertiary"
              onClick={toggleExpandSubSidebar}
              size="md"
            />
          )}
        </Flex>
        <Flex direction="column" alignItems="flex-start" alignSelf="stretch" gap={1} flex="1 0 0">
          {items.map((item: MenuItemsType) => {
            return (
              <Flex
                key={item.label}
                py={1.5}
                px={2}
                alignItems="center"
                alignSelf="stretch"
                gap={2.5}
                direction="column"
              >
                <SubSidebarMenuButton
                  bg={item.bg}
                  label={item.label}
                  icon={item.icon}
                  iconStyles={item.iconStyles}
                  link={item.link}
                  snapshot={item.snapshot}
                  variant={item.variant}
                  criteria={item.criteria}
                  isActive={activeLink === item.link}
                />
                {item.showDivider == true ? (
                  <Divider w="full" borderColor="border.emphasis" />
                ) : null}
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    </motion.div>
  );
}
